import type {
  BinaryFilter,
  DateRange,
  Filter,
  Query,
  QueryOrder,
} from "@cubejs-client/core";
import type { z } from "zod/v4";
import { MAX_LIMIT } from "@hightouch/lib/ad-studio/insights-malloy/constants";
import { leafMemberOf } from "@hightouch/lib/ad-studio/insights-malloy/schemas/cube-query";
import {
  FATIGUE_ROWS_PER_PAGE,
  DRILL_ROWS_PER_PAGE,
  GROUP_ADS_PER_PAGE,
  ROWS_PER_PAGE,
  TIMEZONE,
} from "src/pages/ad-studio/insights/performance/constants";
import { toPageFilters } from "src/pages/ad-studio/insights/performance/filters/utils";
import type {
  ConnectedChannel,
  AccountOption,
  AdViewMeta,
  NonEmptyArray,
  SyncScope,
  AdsQuery,
  ChannelScope,
  ConversionEvents,
  EntityList,
  FatiguePage,
  GroupBy,
  GroupDimension,
  GroupRow,
  InsightsCubeBlend,
  GroupScope,
  GroupValue,
  InsightsCubeQuery,
  InsightsMeta,
  ListQuery,
  MetricCell,
  MetricColumn,
  Reported,
  Paged,
  AdRow,
  PilledAdRow,
  ViewMeta,
  DailyPoint,
  ChildList,
  DrillScope,
  Found,
  AdHeaderRow,
  ParentHeaderRow,
  ParentGrain,
  ValuesByMember,
  EventsByChannel,
  ViewIntent,
} from "src/pages/ad-studio/insights/performance/types";
import {
  covers,
  offersAccounts,
} from "src/pages/ad-studio/insights/performance/utils/channels";
import {
  columnsFor,
  drillTileMetricsOf,
  fatigueMetricsOf,
  groupByOf,
  groupSegmentsOf,
  metricMember,
} from "src/pages/ad-studio/insights/performance/utils/meta";
import type {
  EntityKind,
  ListOf,
  Order,
} from "src/pages/ad-studio/insights/performance/view/types";
import type { FatigueCountsState } from "src/pages/ad-studio/insights/shared/types";
import {
  adDimensions,
  detailedAdDimensions,
  pilledAdDimensions,
  pilledAdRowsSchema,
  dailyColumns,
  entityDimensions,
  headerDimensions,
  adRowsSchema,
  accountsSchema,
  aggregateSchema,
  eventsByChannelSchema,
  valuesByMemberSchema,
  entityRowsSchema,
  fatiguePageSchema,
  fatigueCountsSchema,
  totalSchema,
  groupRowsSchema,
  adHeaderSchema,
  adSetHeaderSchema,
  campaignHeaderSchema,
  dailySchema,
  rowCountSchema,
  adTagsSchema,
  type AdTagCategory,
} from "src/pages/ad-studio/insights/performance/utils/rows";

export { dayRangeSchema } from "src/pages/ad-studio/insights/shared/dates";

export const equals = (
  member: string,
  values: readonly string[],
): BinaryFilter => ({
  member,
  operator: "equals",
  values: [...values],
});

const accountFiltersOf = ({
  view,
  sync,
}: {
  view: ViewMeta;
  sync: SyncScope;
}): readonly Filter[] => {
  switch (sync.kind) {
    case "by-sync": {
      return [];
    }
    case "by-account": {
      return [
        equals(view.controls.channelPicker.account.member, sync.accounts),
      ];
    }
    default: {
      return sync satisfies never;
    }
  }
};

function scopeFilter({
  view,
  scope,
}: {
  view: ViewMeta;
  scope: readonly ChannelScope[];
}): Filter {
  return {
    or: scope
      .flatMap(({ syncs }) => syncs)
      .map((sync) => ({
        and: [
          equals(view.controls.channelPicker.sync, [sync.syncId]),
          ...accountFiltersOf({ view, sync }),
        ],
      })),
  };
}

export function conversionPick({
  view,
  events,
}: {
  view: ViewMeta;
  events: ConversionEvents;
}): readonly Filter[] {
  const picks = [...events].map(
    ([sourceType, values]): Filter => ({
      and: [
        equals(view.controls.channelPicker.conversion.channel, [sourceType]),
        equals(view.controls.channelPicker.conversion.event, values),
      ],
    }),
  );
  return picks.length === 0 ? [] : [{ or: picks }];
}

const timeDimension = (view: ViewMeta, dateRange: DateRange) => ({
  dimension: view.controls.dateRange.date,
  dateRange,
});

export const scoped = ({
  view,
  dates,
  filters,
}: {
  view: ViewMeta;
  dates: DateRange;
  filters: readonly Filter[];
}) => ({
  timeDimensions: [timeDimension(view, dates)],
  timezone: TIMEZONE,
  filters: [...filters],
});

const entityOrder = ({
  view,
  first,
}: {
  view: ViewMeta;
  first: [string, QueryOrder];
}): [string, QueryOrder][] => [
  first,
  [view.entities.entity.id, "asc"],
  [view.controls.channelPicker.sync, "asc"],
];

const paged = ({ limit, offset }: { limit: number; offset: number }) => ({
  limit,
  offset,
  total: true,
});

const withSortMetric = ({
  view,
  metrics,
  order,
}: Pick<ListQuery, "metrics" | "order"> & {
  view: ViewMeta;
}): readonly MetricColumn[] =>
  columnsFor({ view, keys: [...new Set([...metrics, order.key])] });

const membersOfMetrics = (metrics: readonly MetricColumn[]): string[] =>
  metrics.map((column) => column.member);

const unscopedFiltersOf = ({
  query,
  view,
}: {
  query: AdsQuery;
  view: ViewMeta;
}): Filter[] => [
  ...conversionPick({ view, events: query.conversionEvents }),
  ...query.filters,
];

const pageFilters = ({
  query,
  view,
}: {
  query: AdsQuery;
  view: ViewMeta;
}): Filter[] => [
  scopeFilter({ view, scope: query.scope }),
  ...unscopedFiltersOf({ query, view }),
];

const listCube = <Row>({
  query,
  view,
  dimensions,
  segments,
  rows,
}: {
  query: ListQuery;
  view: ViewMeta;
  dimensions: readonly string[];
  segments: ListQuery["segments"];
  rows: z.ZodType<Paged<Row>, unknown>;
}): InsightsCubeQuery<Paged<Row>> => ({
  subject: `list:${view.name}`,
  query: {
    measures: membersOfMetrics(withSortMetric({ ...query, view })),
    dimensions: [...dimensions],
    segments: [...segments],
    ...scoped({
      view,
      dates: query.dates,
      filters: pageFilters({ query, view }),
    }),
    order: entityOrder({
      view,
      first: [metricMember(view, query.order.key), query.order.direction],
    }),
    ...paged(query.page),
  },
  rows,
});

export const adsCubeQuery = ({
  query,
  view,
}: {
  query: ListQuery;
  view: AdViewMeta;
}): InsightsCubeQuery<Paged<AdRow>> =>
  listCube({
    query,
    view,
    dimensions: adDimensions(view),
    segments: query.segments,
    rows: adRowsSchema({ view, metrics: withSortMetric({ ...query, view }) }),
  });

export const adCardsCubeQuery = ({
  query,
  view,
}: {
  query: ListQuery;
  view: AdViewMeta;
}): InsightsCubeQuery<Paged<PilledAdRow>> =>
  listCube({
    query,
    view,
    dimensions: pilledAdDimensions(view),
    segments: query.segments,
    rows: pilledAdRowsSchema({
      view,
      metrics: withSortMetric({ ...query, view }),
    }),
  });

const listed = <Row, Rows extends ListOf>({
  cube,
  listOf,
}: {
  cube: InsightsCubeQuery<Paged<Row>>;
  listOf: (page: Paged<Row>) => Rows;
}): InsightsCubeQuery<Rows> => ({
  subject: cube.subject,
  query: cube.query,
  rows: cube.rows.transform(listOf),
});

const groupedSegmentsOf = ({
  segments,
  view,
  dimension,
}: {
  segments: ListQuery["segments"];
  view: AdViewMeta;
  dimension: GroupDimension;
}): ListQuery["segments"] => [
  ...segments,
  ...groupSegmentsOf({ layout: view.groupBy, member: dimension.name }),
];

export const tabSegmentsOf = ({
  segments,
  groupBy,
}: {
  segments: ListQuery["segments"];
  groupBy: GroupBy;
}): ListQuery["segments"] => {
  switch (groupBy.kind) {
    case "attribute": {
      const grouped = groupSegmentsOf({
        layout: groupBy.view.groupBy,
        member: groupBy.dimension.name,
      });
      return segments.filter((name) => !grouped.includes(name));
    }
    case "ads":
    case "entities": {
      return segments;
    }
    default: {
      return groupBy satisfies never;
    }
  }
};

function groupsCubeQuery({
  query,
  view,
  dimension,
}: {
  query: ListQuery;
  view: AdViewMeta;
  dimension: GroupDimension;
}): InsightsCubeQuery<Paged<GroupRow>> {
  const { groupCard } = view.layouts;
  return {
    subject: `groups:${dimension.name}`,
    query: {
      ...adsCubeQuery({
        query: {
          ...query,
          segments: groupedSegmentsOf({
            segments: query.segments,
            view,
            dimension,
          }),
        },
        view,
      }).query,
      measures: [
        ...membersOfMetrics(withSortMetric({ ...query, view })),
        groupCard.count,
        groupCard.statuses,
        groupCard.objectives,
        groupCard.previews,
      ],
      dimensions: [dimension.name],
      order: [
        [metricMember(view, query.order.key), query.order.direction],
        [dimension.name, "asc"],
      ],
    },
    rows: groupRowsSchema({
      dimension,
      view,
      metrics: withSortMetric({ ...query, view }),
    }),
  };
}

const groupFilter = ({
  dimension,
  value,
}: {
  dimension: GroupDimension;
  value: GroupValue;
}): Filter => {
  switch (value.kind) {
    case "value": {
      return equals(dimension.name, [value.raw]);
    }
    case "no-value": {
      return { member: dimension.name, operator: "notSet" };
    }
    default: {
      return value satisfies never;
    }
  }
};

export function groupAdsQuery({
  scope,
  value,
  page,
}: {
  scope: GroupScope;
  value: GroupValue;
  page: number;
}): InsightsCubeQuery<Paged<AdRow>> {
  const cube = adsCubeQuery({
    query: {
      ...scope.query,
      filters: [
        ...rowFiltersOf({
          filters: scope.query.filters,
          measures: measureNamesOf(scope.meta.views.ad),
        }),
        groupFilter({ dimension: scope.dimension, value }),
      ],
      segments: groupedSegmentsOf({
        segments: scope.query.segments,
        view: scope.meta.views.ad,
        dimension: scope.dimension,
      }),
      page: { limit: GROUP_ADS_PER_PAGE, offset: page * GROUP_ADS_PER_PAGE },
    },
    view: scope.meta.views.ad,
  });
  return { ...cube, subject: `group-ads:${scope.dimension.name}` };
}

export function entityListQuery({
  list,
  query,
}: {
  list: EntityList;
  query: ListQuery;
}): InsightsCubeQuery<ListOf<EntityKind>> {
  switch (list.kind) {
    case "ads": {
      return listed({
        cube: adsCubeQuery({ query, view: list.view }),
        listOf: (page) => ({ kind: "ads", ...page }),
      });
    }
    case "entities": {
      switch (list.grain) {
        case "ad_set": {
          return listed({
            cube: listCube({
              query,
              view: list.view,
              dimensions: entityDimensions.ad_set(list.view),
              segments: [],
              rows: entityRowsSchema.ad_set({
                view: list.view,
                metrics: withSortMetric({ ...query, view: list.view }),
              }),
            }),
            listOf: (page) => ({ kind: "ad-sets", ...page }),
          });
        }
        case "campaign": {
          return listed({
            cube: listCube({
              query,
              view: list.view,
              dimensions: entityDimensions.campaign(list.view),
              segments: [],
              rows: entityRowsSchema.campaign({
                view: list.view,
                metrics: withSortMetric({ ...query, view: list.view }),
              }),
            }),
            listOf: (page) => ({ kind: "campaigns", ...page }),
          });
        }
        default: {
          return list satisfies never;
        }
      }
    }
    default: {
      return list satisfies never;
    }
  }
}

const measureNamesOf = (view: ViewMeta): ReadonlySet<string> =>
  new Set(
    view.folders
      .flatMap((folder) => folder.members)
      .flatMap((member) => (member.kind === "measure" ? [member.name] : [])),
  );

// A group never mixes measures with row fields, so an emptied group was all measures.
const rowFiltersOf = ({
  filters,
  measures,
}: {
  filters: readonly Filter[];
  measures: ReadonlySet<string>;
}): Filter[] =>
  filters.flatMap((filter): Filter[] => {
    if ("and" in filter) {
      const kept = rowFiltersOf({ filters: filter.and, measures });
      return kept.length === 0 ? [] : [{ and: kept }];
    }
    if ("or" in filter) {
      const kept = rowFiltersOf({ filters: filter.or, measures });
      return kept.length === 0 ? [] : [{ or: kept }];
    }
    const member = leafMemberOf(filter);
    return member.kind === "member" && measures.has(member.name)
      ? []
      : [filter];
  });

/** Every row the list's scope, dates, segments and row filters match, whatever its metrics. */
const unrankedOf = ({ list, view }: { list: Query; view: ViewMeta }) => {
  const { segments, timeDimensions, timezone, filters } = list;
  return {
    ...(segments === undefined ? {} : { segments }),
    timeDimensions,
    timezone,
    filters: rowFiltersOf({
      filters: filters ?? [],
      measures: measureNamesOf(view),
    }),
  };
};

/** One row aggregating everything the list matched, measure filters aside. */
export const aggregateOf = ({
  list,
  view,
  metrics,
}: {
  list: Query;
  view: ViewMeta;
  metrics: readonly MetricColumn[];
}): InsightsCubeQuery<Found<readonly MetricCell[]>> => ({
  subject: `aggregate:${view.name}`,
  query: {
    measures: membersOfMetrics(metrics),
    ...unrankedOf({ list, view }),
  },
  rows: aggregateSchema(metrics),
});

/** How many of the aggregate's rows come from the channels in scope. */
export const rowCountOf = ({
  list,
  view,
  scope,
}: {
  list: Query;
  view: ViewMeta;
  scope: readonly ChannelScope[];
}): InsightsCubeQuery<number> => {
  const unranked = unrankedOf({ list, view });
  return {
    subject: `row-count:${view.name}`,
    query: {
      dimensions: list.dimensions,
      ...unranked,
      filters: [...unranked.filters, scopeFilter({ view, scope })],
      limit: 1,
      total: true,
    },
    rows: rowCountSchema,
  };
};

export function resultsQuery({
  groupBy,
  query,
}: {
  groupBy: GroupBy;
  query: ListQuery;
}): InsightsCubeQuery<ListOf> {
  switch (groupBy.kind) {
    case "ads":
    case "entities": {
      return entityListQuery({ list: groupBy, query });
    }
    case "attribute": {
      return listed({
        cube: groupsCubeQuery({
          query,
          view: groupBy.view,
          dimension: groupBy.dimension,
        }),
        listOf: (page): ListOf => ({
          kind: "groups",
          dimension: groupBy.dimension,
          ...page,
        }),
      });
    }
    default: {
      return groupBy satisfies never;
    }
  }
}

/** The tab's list, its rows read with the metrics they were fetched with. */
export const tabListCube = ({
  groupBy,
  query,
}: {
  groupBy: GroupBy;
  query: ListQuery;
}) => {
  const cube = resultsQuery({ groupBy, query });
  const listKey = JSON.stringify(
    resultsQuery({
      groupBy,
      query: { ...query, page: { ...query.page, offset: 0 } },
    }).query,
  );
  return {
    ...cube,
    listKey,
    rows: cube.rows.transform((rows) => ({
      rows,
      metrics: query.metrics,
      listKey,
    })),
  };
};

export type SavedQuery = Omit<Query, "timezone" | "limit" | "offset" | "total">;

export const toListQuery = ({
  intent,
  view,
  page,
}: {
  intent: ViewIntent;
  view: ViewMeta;
  page: ListQuery["page"];
}): ListQuery => ({
  scope: intent.selection.scope,
  conversionEvents: intent.selection.conversionEvents,
  dates: intent.dates,
  filters: toPageFilters({ root: intent.filters, view }),
  order: intent.metricSettings.order,
  segments: intent.segments,
  metrics: intent.metricSettings.metrics,
  page,
});

const savedQueryOf = ({
  timezone: _timezone,
  limit: _limit,
  offset: _offset,
  total: _total,
  ...saved
}: Query): SavedQuery => saved;

/** The query a saved view stores for its intent, its scope written out sync by sync. */
const savedListOf = ({
  meta,
  intent,
}: {
  meta: InsightsMeta;
  intent: ViewIntent;
}) => {
  const groupBy = groupByOf({ views: meta.views, key: intent.groupByKey });
  const query = toListQuery({
    intent,
    view: groupBy.view,
    page: { limit: ROWS_PER_PAGE, offset: 0 },
  });
  return {
    groupBy,
    query,
    saved: savedQueryOf(resultsQuery({ groupBy, query }).query),
  };
};

export const toScopedSavedQuery = ({
  meta,
  intent,
}: {
  meta: InsightsMeta;
  intent: ViewIntent;
}): SavedQuery => savedListOf({ meta, intent }).saved;

// A selection of every connected sync is stored with no scope filter, so the view also reads syncs connected later.
export const toSavedQuery = ({
  meta,
  connected,
  intent,
}: {
  meta: InsightsMeta;
  connected: NonEmptyArray<ConnectedChannel>;
  intent: ViewIntent;
}): SavedQuery => {
  const { groupBy, query, saved } = savedListOf({ meta, intent });
  return covers({ scope: intent.selection.scope, channels: connected })
    ? { ...saved, filters: unscopedFiltersOf({ query, view: groupBy.view }) }
    : saved;
};

const fatigueFilters = ({
  query,
  view,
}: {
  query: AdsQuery;
  view: AdViewMeta;
}): Filter[] => [
  ...pageFilters({ query, view }),
  equals(view.layouts.fatigue.status, view.layouts.fatigue.fatiguing),
];

export function fatigueCubeQuery({
  query,
  view,
  order,
  page,
}: {
  query: AdsQuery;
  view: AdViewMeta;
  order: Order<string>;
  page: number;
}): InsightsCubeQuery<FatiguePage> {
  return {
    subject: `fatigue:${view.name}`,
    query: {
      measures: membersOfMetrics(fatigueMetricsOf(view)),
      dimensions: [...detailedAdDimensions(view), view.entities.campaign.name],
      segments: [...query.segments],
      ...scoped({
        view,
        dates: query.dates,
        filters: fatigueFilters({ query, view }),
      }),
      order: entityOrder({ view, first: [order.key, order.direction] }),
      ...paged({
        limit: FATIGUE_ROWS_PER_PAGE,
        offset: page * FATIGUE_ROWS_PER_PAGE,
      }),
    },
    rows: fatiguePageSchema(view),
  };
}

export function fatigueCountsQuery({
  query,
  view,
}: {
  query: AdsQuery;
  view: AdViewMeta;
}): InsightsCubeQuery<FatigueCountsState> {
  return {
    subject: `fatigue-counts:${view.name}`,
    query: {
      measures: [view.layouts.groupCard.count],
      dimensions: [view.layouts.fatigue.status],
      segments: [...query.segments],
      ...scoped({
        view,
        dates: query.dates,
        filters: fatigueFilters({ query, view }),
      }),
    },
    rows: fatigueCountsSchema(view),
  };
}

export function totalSpendQuery({
  query,
  view,
}: {
  query: AdsQuery;
  view: AdViewMeta;
}): InsightsCubeQuery<Reported> {
  const spend = view.layouts.fatigue.spend;
  return {
    subject: `total-spend:${view.name}`,
    query: {
      measures: [spend],
      segments: [...query.segments],
      ...scoped({
        view,
        dates: query.dates,
        filters: pageFilters({ query, view }),
      }),
    },
    rows: totalSchema(spend),
  };
}

const memberValuesQueries = ({
  view,
  members: [first, ...rest],
  within,
  dates,
}: {
  view: ViewMeta;
  members: NonEmptyArray<string>;
  within: readonly Filter[];
  dates: DateRange;
}): NonEmptyArray<Query> => {
  const valuesOf = (member: string): Query => ({
    dimensions: [member],
    ...scoped({ view, dates, filters: within }),
    limit: MAX_LIMIT,
  });
  return [valuesOf(first), ...rest.map(valuesOf)];
};

export function valuesBlend({
  view,
  members,
  within,
  dates,
}: {
  view: ViewMeta;
  members: NonEmptyArray<string>;
  within: readonly Filter[];
  dates: DateRange;
}): InsightsCubeBlend<ValuesByMember> {
  return {
    subject: `values:${members.join(",")}:${JSON.stringify(within)}`,
    queries: memberValuesQueries({ view, members, within, dates }),
    rows: valuesByMemberSchema({ members, limit: MAX_LIMIT }),
  };
}

export function eventOptionsQuery({
  view,
  scope,
  dates,
}: {
  view: ViewMeta;
  scope: NonEmptyArray<ChannelScope>;
  dates: DateRange;
}): InsightsCubeQuery<EventsByChannel> {
  const { conversion } = view.controls.channelPicker;
  return {
    subject: `event-options:${scope.map(({ channel }) => channel.sourceType).join(",")}`,
    query: {
      measures: [conversion.count],
      dimensions: [conversion.channel, conversion.event],
      ...scoped({ view, dates, filters: [scopeFilter({ view, scope })] }),
      limit: MAX_LIMIT,
    },
    rows: eventsByChannelSchema({
      channel: conversion.channel,
      event: conversion.event,
      conversions: conversion.count,
      limit: MAX_LIMIT,
    }),
  };
}

const EVERY_ACCOUNT_WITH_DATA_SINCE = "2000-01-01";

export const accountsQuery = ({
  view,
  syncIds,
  today,
}: {
  view: ViewMeta;
  syncIds: NonEmptyArray<string>;
  today: string;
}): InsightsCubeQuery<ReadonlyMap<string, readonly AccountOption[]>> => {
  const { sync, account } = view.controls.channelPicker;
  return {
    subject: "accounts",
    query: {
      dimensions: [sync, account.member, account.name],
      ...scoped({
        view,
        dates: [EVERY_ACCOUNT_WITH_DATA_SINCE, today],
        filters: [equals(sync, syncIds)],
      }),
      order: [
        [sync, "asc"],
        [account.name, "asc"],
      ],
    },
    rows: accountsSchema(view),
  };
};

const accountSyncIdsOf = ({ views, syncs }: InsightsMeta): readonly string[] =>
  syncs.flatMap((sync) =>
    offersAccounts({ view: views.ad, channel: sync.channel }) ? [sync.id] : [],
  );

/** The one accounts list for every sync whose channel offers accounts, if any does. */
export const accountCubesOf = ({
  meta,
  today,
}: {
  meta: InsightsMeta;
  today: string;
}) => {
  const [first, ...rest] = accountSyncIdsOf(meta);
  const view = meta.views.ad;
  return first === undefined
    ? []
    : [
        {
          cube: accountsQuery({ view, syncIds: [first, ...rest], today }),
          wire: view.wire,
        },
      ];
};

export const syncFilters = ({
  view,
  scope,
}: {
  view: ViewMeta;
  scope: readonly ChannelScope[];
}): readonly Filter[] => [
  equals(
    view.controls.channelPicker.sync,
    scope.flatMap(({ syncs }) => syncs.map(({ syncId }) => syncId)),
  ),
];

const drillFilters = ({
  view,
  drill,
}: {
  view: ViewMeta;
  drill: DrillScope;
}): Filter[] => [
  scopeFilter({ view, scope: drill.query.scope }),
  ...conversionPick({ view, events: drill.query.conversionEvents }),
];

const entityFilters = ({
  drill,
  view,
  id,
}: {
  drill: DrillScope;
  view: ViewMeta;
  id: string;
}): Filter[] => [
  equals(view.entities.entity.id, [id]),
  ...drillFilters({ view, drill }),
];

const entityScope = ({
  drill,
  view,
  id,
}: {
  drill: DrillScope;
  view: ViewMeta;
  id: string;
}) =>
  scoped({
    view,
    dates: drill.query.dates,
    filters: entityFilters({ drill, view, id }),
  });

const headerCube = ({
  drill,
  view,
  id,
  dimensions,
}: {
  drill: DrillScope;
  view: ViewMeta;
  id: string;
  dimensions: readonly string[];
}): Query => ({
  measures: membersOfMetrics(drillTileMetricsOf(view)),
  dimensions: [...dimensions],
  ...entityScope({ drill, view, id }),
});

export const adHeaderQuery = ({
  drill,
  id,
}: {
  drill: DrillScope;
  id: string;
}): InsightsCubeQuery<Found<AdHeaderRow>> => {
  const view = drill.meta.views.ad;
  return {
    subject: `ad-header:${id}`,
    query: headerCube({
      drill,
      view,
      id,
      dimensions: headerDimensions.ad(view),
    }),
    rows: adHeaderSchema(view),
  };
};

export const adTagsSubject = (id: string) => `ad-tags:${id}`;

export const adTagsBlend = ({
  drill,
  id,
  categories,
}: {
  drill: DrillScope;
  id: string;
  categories: NonEmptyArray<AdTagCategory>;
}): InsightsCubeBlend<readonly string[]> => {
  const view = drill.meta.views.ad;
  const [first, ...rest] = categories;
  return {
    subject: adTagsSubject(id),
    queries: memberValuesQueries({
      view,
      members: [first.name, ...rest.map((category) => category.name)],
      within: entityFilters({ drill, view, id }),
      dates: drill.query.dates,
    }),
    rows: adTagsSchema({ categories, limit: MAX_LIMIT }),
  };
};

export function parentHeaderQuery({
  drill,
  grain,
  id,
}: {
  drill: DrillScope;
  grain: ParentGrain;
  id: string;
}): InsightsCubeQuery<Found<ParentHeaderRow>> {
  const subject = `${grain}-header:${id}`;
  const { views } = drill.meta;
  switch (grain) {
    case "ad_set": {
      const view = views.ad_set;
      return {
        subject,
        query: headerCube({
          drill,
          view,
          id,
          dimensions: headerDimensions.ad_set(view),
        }),
        rows: adSetHeaderSchema(view),
      };
    }
    case "campaign": {
      const view = views.campaign;
      return {
        subject,
        query: headerCube({
          drill,
          view,
          id,
          dimensions: headerDimensions.campaign(view),
        }),
        rows: campaignHeaderSchema(view),
      };
    }
    default: {
      return grain satisfies never;
    }
  }
}

export const CHILD_GRAIN = { ad_set: "ad", campaign: "ad_set" } as const;

export const childViewOf = (drill: DrillScope, parent: ParentGrain): ViewMeta =>
  drill.meta.views[CHILD_GRAIN[parent]];

export function entityChildrenQuery({
  drill,
  parent,
  list,
  order,
  page,
}: {
  drill: DrillScope;
  parent: { grain: ParentGrain; id: string };
  list: ChildList;
  order: AdsQuery["order"];
  page: number;
}): InsightsCubeQuery<ListOf<EntityKind>> {
  const subject = `children:${parent.grain}:${parent.id}`;
  const metricsOf = (view: ViewMeta): readonly MetricColumn[] =>
    withSortMetric({
      view,
      metrics: list.metrics.map((column) => column.key),
      order,
    });
  const childQuery = ({
    view,
    parentId,
    dimensions,
  }: {
    view: ViewMeta;
    parentId: string;
    dimensions: readonly string[];
  }): Query => ({
    measures: membersOfMetrics(metricsOf(view)),
    dimensions: [...dimensions],
    segments: [...list.segments],
    ...scoped({
      view,
      dates: drill.query.dates,
      filters: [
        equals(parentId, [parent.id]),
        ...drillFilters({ view, drill }),
        ...list.filters,
      ],
    }),
    order: entityOrder({
      view,
      first: [metricMember(view, order.key), order.direction],
    }),
    ...paged({
      limit: DRILL_ROWS_PER_PAGE,
      offset: page * DRILL_ROWS_PER_PAGE,
    }),
  });
  switch (parent.grain) {
    case "ad_set": {
      const view = drill.meta.views.ad;
      const query = childQuery({
        view,
        parentId: view.entities.adSet.id,
        dimensions: adDimensions(view),
      });
      return listed({
        cube: {
          subject,
          query,
          rows: adRowsSchema({ view, metrics: metricsOf(view) }),
        },
        listOf: (rows): ListOf<EntityKind> => ({ kind: "ads", ...rows }),
      });
    }
    case "campaign": {
      const view = drill.meta.views.ad_set;
      return listed({
        cube: {
          subject,
          query: childQuery({
            view,
            parentId: view.entities.campaign.id,
            dimensions: entityDimensions.ad_set(view),
          }),
          rows: entityRowsSchema.ad_set({ view, metrics: metricsOf(view) }),
        },
        listOf: (rows): ListOf<EntityKind> => ({ kind: "ad-sets", ...rows }),
      });
    }
    default: {
      return parent.grain satisfies never;
    }
  }
}

export function adDailyQuery({
  drill,
  id,
}: {
  drill: DrillScope;
  id: string;
}): InsightsCubeQuery<readonly DailyPoint[]> {
  const view = drill.meta.views.ad;
  const { ctr, frequency } = dailyColumns(view);
  const scope = entityScope({ drill, view, id });
  return {
    subject: `ad-daily:${id}`,
    query: {
      measures: [ctr.member, frequency.member],
      ...scope,
      timeDimensions: scope.timeDimensions.map((dimension) => ({
        ...dimension,
        granularity: "day",
      })),
      order: [[view.controls.dateRange.date, "asc"]],
    },
    rows: dailySchema(view),
  };
}
